import { useEffect, useMemo, createContext, useContext, useState } from "react";

import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import PollerGroup, {
  GroupCycleInfo
} from "src/organization/pages/project/environment/metrics/utils/polling/PollerGroup";

import usePollerFocus from "./usePollerFocus";
import { initLoadingOption } from "./utils";

type Props = {
  timeFrame: TimeFrameType;
  interval?: number;
};

const usePollgroup = ({ timeFrame, interval }: Props) => {
  const [status, onLoadingChange] = useState(initLoadingOption());
  const [cycle, onCycle] = useState<GroupCycleInfo>({
    lastUpdated: new Date()
  });

  const pollgroup = useMemo(
    () =>
      new PollerGroup({
        onCycle,
        onLoadingChange: (option, timeFrame) => {
          onLoadingChange(option);
          const type = option.status.type;
          const isRange = !timeFrame.id;
          if (option.isLoading || type === "refresh" || isRange) return;
          // For relative time frames identified by those having id as defind 1d:"15m" etc,
          // the timeFrame object itself doesn't change between polls. We manually
          // update a hash to force downstream dependencies (like React hooks) to
          // recognize a change and re-trigger their logic.
          timeFrame.hash = Date.now().toString();
        }
      }),
    []
  );

  usePollerFocus(pollgroup);

  useEffect(() => {
    timeFrame.id
      ? pollgroup.startPolling(timeFrame, "refresh", interval)
      : pollgroup.pollOnce(timeFrame);
  }, [pollgroup, timeFrame, interval]);

  useEffect(() => {
    return () => pollgroup.unsubscribe();
  }, [pollgroup]);

  return { pollgroup, cycle, status };
};

type PollgroupContextType = {
  pollgroup: PollerGroup;
};

export const PollgroupContext = createContext<PollgroupContextType>(
  undefined as any
);

export const usePollgroupContext = () => {
  return useContext(PollgroupContext);
};

export default usePollgroup;
